<template>
  <div v-if="hasWorkOrder">
    <div class="pa-1 pa-lg-4 d-flex justify-center justify-lg-space-between align-center">
      <v-row align="center">
        <v-col>
          <ActionButtons
            v-if="activeView === 'gridView' || activeView === 'treeView'"
            :actionButtons="getActionButtons"
            :allowActionButtonsBreakpoint="allowActionButtonsBreakpoint"
          />
        </v-col>
        <v-col>
          <v-row justify="end">
            <v-col
              cols="auto"
              class="p-0"
            >
              <v-btn
                icon
                :color="getActiveColor('gridView')"
                data-cy="cy-grid-view"
                @click="onClickToggleActiveView('gridView')"
              >
                <v-icon>
                  grid_on
                </v-icon>
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              class="p-0"
            >
              <v-btn
                icon
                :color="getActiveColor('treeView')"
                data-cy="cy-tree-view"
                @click="onClickToggleActiveView('treeView')"
              >
                <v-icon>
                  account_tree
                </v-icon>
              </v-btn>
            </v-col>
            <v-col
              cols="auto"
              class="p-0"
            >
              <v-btn
                icon
                :color="getActiveColor('analysisView')"
                @click="onClickToggleActiveView('analysisView')"
              >
                <v-icon>
                  format_list_bulleted
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <div v-else>
      <!-- Tree View -->
      <TreeService
        v-if="activeView === 'gridView' || activeView === 'treeView'"
        :id="workOrder.site.ref"
        :name="workOrder.site.name"
        :active="workOrder.site.active"
        :nodeItem="site"
        :nodes="mutableTree"
        nodeType="Site"
        :mapComponentIdToServices="mapComponentIdToServices"
        :mapAnalyisTypesByRef="mapAnalyisTypesByRef"
        :site="workOrder.site"
        :showAll="showAll"
        :activeView="activeView"
        :tables="tables"
        @refresh-data="refreshData"
        @nodeItemChanged="nodeItemChanged"
        @getTables="getTables"
      />
      <!-- Analysis View -->
      <AnalysisView
        v-if="activeView === 'analysisView'"
        :analyses="mapComponentIdToServices"
        :nodes="mutableTree"
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import EventBus from '@/utilities/event-bus';
import TreeService from '@/components/services/TreeService';
import AnalysisView from '@/components/services/AnalysisView.vue';
import AddAnalysisForm from '@/components/component/AddAnalysisForm';
import ActionButtons from '@/components/shared/ActionButtons';
import nodeMixim from '@/mixins/NodeMixim';
import { CONTAINER } from '@/aquassist-shared/constants/types/node';

export default {
    components: {
        ActionButtons,
        TreeService,
        AnalysisView,
    },
    mixins: [
        nodeMixim,
    ],
    props: {
        allowActionButtonsBreakpoint: {
            type: Boolean,
            default: true,
        },
        tables: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            showAll: true,
            // services represent either samples or analyses linked to a component in particular
            mapComponentIdToServices: {},
            mapAllComponentsByComponentRef: {},
            mapAnalyisTypesByRef: {},
            activeView: 'gridView',
            mutableTree: [],
            site: {},
        };
    },
    computed: {
        ...mapGetters([
            'currentMember',
            'workOrder',
            'hasPermission',
        ]),
        getActionButtons() {
            return [
                {
                    id: 'btn-collapse-all',
                    label: this.showAll ? this.$t('tree.collapseAll') : this.$t('tree.expandAll'),
                    action: this.toggleShowAll,
                    disabled: !this.hasPermission('nodes.children.view'),
                    icon: 'mdi-expand-all',
                    color: 'primary',
                },
            ];
        },
        hasWorkOrder() {
            return Object.keys(this.workOrder).length !== 0;
        },
        currentActivity() {
            return this.workOrder.activities.measuresAndSamplesCollect;
        },
        analyses() {
            return this.currentActivity.tasks.analyses.map(({ analysisType, ...props }) => {
                let type = analysisType;

                if (analysisType && analysisType.ref) {
                    type = this.mapAnalyisTypesByRef[analysisType.ref];
                }

                return {
                    ...props,
                    analysisType: type,
                };
            });
        },
    },
    watch: {
        workOrder: {
            async handler() {
                if (Object.keys(this.workOrder).length === 0) {
                    return;
                }

                if (!this.mutableTree.length) {
                    // the first iteration, when we initialize the component
                    const children = this.mapChildren([...this.currentActivity.tree]);
                    this.mutableTree = children;
                } else {
                    // when we reload the WO
                    const treeCopy = cloneDeep(this.currentActivity.tree);
                    const nodes = this.flatMapAllNodes(this.mutableTree);
                    const childrenWithStatus = treeCopy.map(node => this.setChildrenShowStatus(node, nodes));
                    this.mutableTree = childrenWithStatus;
                }

                this.buildMapComponentIdToServices();
                this.buildMapOfAllComponents(this.currentActivity.tree);
                this.buildMapOfAnalysisTypesByRef(this.currentActivity.analysisTypes);
            },
            immediate: true,
        },
    },
    mounted() {
        this.site = { ...this.workOrder.site, showChildren: true };
        EventBus.$on('node-action', data => {
            this.showModal(data[0], Object.assign(data[1]), { siteId: this.workOrder.site.ref });
        });
    },
    destroyed() {
        EventBus.$off('node-action');
    },
    methods: {
        ...mapActions({
            reloadWorkOrder: 'getWorkOrderInfo',
            setMeasuresInCache: 'setMeasuresInCache',
        }),
        async onClickToggleActiveView(activeView) {
            this.activeView = activeView;
            await this.reloadWorkOrder(this.workOrder.id);
        },
        toggleShowAll() {
            this.showAll = !this.showAll;
        },
        buildMapComponentIdToServices() {
            this.mapComponentIdToServices = {};

            this.currentActivity.tasks.analyses.forEach(analysis => {
                const id = analysis.source.ref;

                if (!this.mapComponentIdToServices[id]) {
                    this.mapComponentIdToServices[id] = {};
                }

                if (!this.mapComponentIdToServices[id].analyses) {
                    this.mapComponentIdToServices[id].analyses = [];
                }

                const analysisType = this.currentActivity.analysisTypes.find(({ ref }) => ref === analysis.analysisType.ref);
                this.mapComponentIdToServices[id].analyses.push({
                    ...analysis,
                    analysisType,
                });

                // sort by displayOrder
                this.mapComponentIdToServices[id].analyses = this.mapComponentIdToServices[id].analyses
                    .sort((a, b) => a.analysisType.displayOrder - b.analysisType.displayOrder);
            });
        },
        buildMapOfAllComponents(trees) {
            const mapOfComponents = {};
            trees.forEach(tree => Object.assign(mapOfComponents, this.buildMapOfComponentsPerTree(tree)));
            this.mapAllComponentsByComponentRef = mapOfComponents;
        },
        buildMapOfComponentsPerTree(tree, components) {
            const mapOfComponents = components ? cloneDeep(components) : {};

            mapOfComponents[tree.ref] = { ...tree };
            delete mapOfComponents[tree.ref].children;

            if (tree.children) {
                tree.children.forEach(node => Object.assign(
                    mapOfComponents,
                    this.buildMapOfComponentsPerTree(node, mapOfComponents),
                    { parentNode: mapOfComponents[tree.ref] },
                ));
            } else {
                Object.assign(mapOfComponents[tree.ref], { parentNode: mapOfComponents[tree.ref] });
            }

            return mapOfComponents;
        },
        buildMapOfAnalysisTypesByRef(analyseTypesArray) {
            this.mapAnalyisTypesByRef = analyseTypesArray.reduce((analysisTypesByRef, type) => ({
                ...cloneDeep(analysisTypesByRef),
                [type.ref]: type,
            }), {});
        },
        async refreshData() {
            await this.reloadWorkOrder(this.workOrder.id);
        },
        showModal(action, node) {
            if (action === 'addAnalysisRequest') {
                this.showAddAnalysisRequestModal(node);
            } else if (action === 'addAnalysis') {
                this.showAddAnalysisModal(node);
            }
        },
        showAddAnalysisModal(node) {
            this.$root.$emit('showModal', {
                title: this.$t('sites.nodes.addAnalysis'),
                payload: {
                    node: Object.assign(node, {
                        alreadyUsedAnalysisTypes: this.mapComponentIdToServices[node.ref] ? this.mapComponentIdToServices[node.ref].analyses : [],
                    }),
                    itemValue: null,
                },
                component: Vue.component('addAnalysis', AddAnalysisForm),
                onConfirm: requestPayload => this.addAnalysis(requestPayload),
            });
        },
        async addAnalysis(requestPayload) {
            const bodyParams = {
                component: requestPayload.componentId,
                norm: {},
            };
            const { data } = await this.$rq.getComponentConfigurations(requestPayload.componentId);
            for (let i = 0; i < data.configurations.length; i += 1) {
                const config = data.configurations[i];
                const configuration = requestPayload.analyses.find(({ id }) => id === config.analysisType.id);
                if (configuration) {
                    bodyParams.norm = config.norm;
                    break;
                }
            }
            const analysisIds = requestPayload.analyses.map(analysis => analysis.id);
            if (analysisIds.length) {
                await this.$rq.addWorkOrderManyAnalysisOnComponent(
                    this.workOrder.id,
                    this.currentActivity.id,
                    { ...bodyParams, analyses: analysisIds },
                );
                this.$toasted.global.toast_success({
                    message: this.$t('msg.analysisAdded', { count: analysisIds.length }),
                });
            }

            await this.refreshData();
            // TODO the scrolleToId doesn't work for analysis
        },
        getActiveColor(view) {
            return this.activeView === view ? 'secondary' : '#26384B';
        },
        mapChildren(children) {
            return children.map(node => ({
                ...node,
                children: node.children ? this.mapChildren(node.children) : undefined,
                showChildren: node.nodeType === CONTAINER,
            }));
        },
        nodeItemChanged(value) {
            const allNodes = [];
            this.mutableTree.forEach((node, index) => {
                const children = this.replaceNode(node, value);
                const nn = node.ref === value.ref ? value : node;
                allNodes[index] = { ...nn, children };
            });
            this.mutableTree = [...allNodes];
            if (value.nodeType === 'Site') {
                this.site = { ...this.site, showChildren: value.showChildren };
            }
        },
        getTables() {
            this.$emit('getTables');
        },
    },
};
</script>
